import type { BundlePropType, SubscriptionCardType } from '@commerce-product-webcomponents/ui';
import { SubcriptionPlanCMSResponseType, Product as SOPProduct } from '@commerce-subscription-webcomponents/sdk';
import { DateFormats, formatDate } from '@nextgen-web-framework/core';
import { OptionMap } from '@commerce-subscription-webcomponents/sdk/dist/es/services/models/OptionMap';
import { Option, VariantGroup } from '@commerce-product-webcomponents/sdk';
import {
  AccountProcessType,
  AmwayUserDetails,
  CartEntry,
  ExceptionsEnum,
  ListEntry,
  Product,
  PromotionResult,
  PromotionResults,
  SlotEntry,
  SopElementResponse,
  VariantDimensions,
} from '../services';
import { CartEntryDetails } from '../services/models/CartEntryDetails';
import { InvalidProductException } from '../types';
import { Modification } from '../services/models/Modification';
import { CartResponse } from '../services/models/CartResponse';
import { SubCartDetail } from '../services/models/SubCartDetail';
import { AccountProductType } from '../services/models/AccountProductType';
import { includesAny, StockStatusEnum } from './cartCommonHelpers';

export type InventoryErrorProps = {
  entry: CartEntry;
  RibbonType: string;
  translation: Function;
  onCallbacktypeClick?: (id?: string, cartEntryData?: CartEntry) => void;
};

export const createProductEntryKey = (entry: CartEntryDetails, amwayUser?: AmwayUserDetails) => {
  if (entry.product.variantDimensions && entry.product.variantDimensions?.length > 0) {
    return (
      entry.entryId + String(entry.quantity) + entry.product.variantDimensions[0].value + String(amwayUser?.accountId)
    );
  }
  return entry.entryId + String(entry.quantity) + String(amwayUser?.accountId);
};

export const getBundleEntriesAndChildPromoitions = (
  entry: CartEntryDetails,
  unavailableText?: string,
  notEligibleText?: string,
) => {
  const newBundleEntries: SlotEntry[][] = [];
  const childPromotions: PromotionResult[] = [];
  let bundleEntries: SlotEntry[] = [];
  const productBundle: BundlePropType[] = [];

  if (entry.bundleSlots) {
    entry.bundleSlots.forEach((slot) => {
      if (slot.entries) {
        newBundleEntries.push(slot.entries);
      }
    });
  }

  bundleEntries = newBundleEntries.flat(4);

  bundleEntries.forEach((entry: SlotEntry) => {
    if (Number(entry?.promotionResults?.firedPromotions?.length) > 0) {
      childPromotions.push(...(entry?.promotionResults?.firedPromotions ?? []));
    }
    if (entry.product) {
      const checkoutRestricted = entry.product?.flags?.restrictFromCheckout;
      const cartPromoRestricted = entry.product.flags?.restrictFromCartLevelPromotion;
      productBundle.push({
        brandName: entry.product.brand ?? '',
        productName: entry.product.name ?? '',
        link: entry.product.cartIconPublicUrl ?? '',
        alt: entry.product.cartIconAltText ?? '',
        quantity: entry.quantity,
        servingSize: entry.product.copyServingSize ?? '',
        productId: entry.product.id,
        isPromotionApplied: entry?.enablePromotionTag,
        slotId: entry.id,
        baseQuantity: entry.baseQuantity,
        isUnavailable: checkoutRestricted || cartPromoRestricted,
        unavailableText: checkoutRestricted ? unavailableText : notEligibleText,
        showError: !!checkoutRestricted,
        derivedAvailablityStatus: entry.product?.derivedAvailabilityStatus,
        firstAvailableFutureDate:
          entry.product?.firstAvailableFutureDate && String(entry.product?.firstAvailableFutureDate),
      });
    }
  });
  return {
    bundleEntries: productBundle,
    childPromotions,
  };
};

export const getVariantName = (variantDimensions: Array<VariantDimensions>) => {
  let variant = '';
  if (variantDimensions && variantDimensions.length > 0) {
    variantDimensions.forEach((v, i) => {
      if (i >= 1) {
        variant += ' - ';
      }
      variant += v.value;
    });
  }

  return variant;
};

export const getStockStatus = (entry: CartEntryDetails | ListEntry) =>
  entry.product.derivedAvailabilityStatus as string;

export const getPointValue = (entry: CartEntryDetails) => String(entry.totalPrice?.amwayValue?.pointValue);

export const getBusinessValue = (entry: CartEntryDetails) => String(entry.totalPrice?.amwayValue?.businessVolume);

export const getStrikeThroughPrice = (entry: CartEntryDetails) => {
  if (entry.totalPriceWithoutDiscount?.value == entry.totalPrice?.value) {
    return undefined;
  } else {
    return entry.totalPriceWithoutDiscount?.value;
  }
};

export const isNthItemAGift = (index: number, cartEntries: CartEntryDetails[]) => {
  const nextEntry = (cartEntries as CartEntryDetails[])[index];
  return nextEntry && nextEntry.isFreeItem;
};

export const createBorderClass = (
  entry: CartEntryDetails,
  index: number,
  entriesLength: number,
  cartEntries: CartEntryDetails[],
  topPaddingStyle?: string,
  freeItemSeperatorStyle?: string,
  endSeperatorStyle?: string,
) => {
  const topPadding = entry.isFreeItem && isNthItemAGift(index - 1, cartEntries) ? topPaddingStyle : '';
  const freeItemSeparatorClass =
    entry.isFreeItem && isNthItemAGift(index + 1, cartEntries) ? '' : freeItemSeperatorStyle;
  const endSeperatorClass = index == entriesLength - 1 ? endSeperatorStyle : '';
  return `${freeItemSeparatorClass} ${endSeperatorClass} ${topPadding}`;
};

export const getExceptionProducts = (entries: CartEntryDetails[]) => {
  const { Anonymous, NonAbo, SpecifiedUser, AccountType, AwardLevel, LengthOfAffiliation } = ExceptionsEnum.Exceptions;

  const validKeys: string[] = [Anonymous, NonAbo, SpecifiedUser, AccountType, AwardLevel, LengthOfAffiliation];

  const removedProducts: CartEntryDetails[] = [];
  const invalidProducts: CartEntryDetails[] = [];

  entries?.forEach((entry) => {
    if (entry.errors) {
      const hasValidKey = entry.errors.some((error) => error.title && validKeys.includes(error.title));
      const hasInvalidProduct = entry.errors.some((error) => error.title === InvalidProductException.INVALID_PRODUCT);

      if (hasValidKey) {
        removedProducts.push(entry);
      }
      if (hasInvalidProduct) {
        invalidProducts.push(entry);
      }
    }
  });

  return {
    removedProducts,
    invalidProducts,
  };
};

export const getAllEntries = (entries?: CartEntryDetails[]) => {
  const withSOPEntry: CartEntryDetails[] = [];
  const withoutSOPEntry: CartEntryDetails[] = [];

  entries &&
    entries.length !== 0 &&
    entries.forEach((entry) =>
      entry.sopElementId !== undefined ? withSOPEntry.push(entry) : withoutSOPEntry.push(entry),
    );
  return { withSOPEntry, withoutSOPEntry };
};

export const getVariantProps = (entry: CartEntryDetails, handleDropdown?: (cartEntry: CartEntryDetails) => void) => {
  const { product } = entry;
  if (entry.allowProductVariantChange) {
    return {
      variantName: product.variantDimensions?.[0]?.value,
      productType: product.type ?? '',
      handleDropdown: () => {
        handleDropdown?.(entry);
      },
    };
  }
  return {};
};

export const transformedSOPEntries = (
  sopEntries: CartEntryDetails[],
  sopElement: SopElementResponse,
  isABOUser: boolean,
  locale?: string,
  unavailableText?: string,
  notEligibleText?: string,
  handleDropdown?: (cartEntry: CartEntryDetails) => void,
) => {
  const sopProducts: SubscriptionCardType[] = [];
  let isOOS = false;

  sopEntries?.map((entry) => {
    if (entry.entryId && sopElement.entryIds?.includes(entry.entryId)) {
      const { bundleEntries } = getBundleEntriesAndChildPromoitions(entry);
      const { product, totalPrice, totalPriceWithoutDiscount, quantity } = entry;
      if (product?.derivedAvailabilityStatus === StockStatusEnum.TNA) {
        isOOS = true;
      }

      const checkoutRestricted = product?.flags?.restrictFromCheckout;
      const cartPromoRestricted = product?.flags?.restrictFromCartLevelPromotion;

      sopProducts.push({
        brandName: product.brand as string,
        productName: product.name as string,
        productId: product.id as string,
        link: product.cartIconPublicUrl as string,
        alt: product.cartIconPublicUrl as string,
        quantity: quantity,
        pointValue: isABOUser ? getPointValue(entry) : '',
        businessVolume: isABOUser ? getBusinessValue(entry) : '',
        price: totalPrice?.value as number,
        currency: totalPrice?.currency as string,
        strikeThroughPrice: totalPriceWithoutDiscount?.value as number,
        derivedAvailablityStatus: product.derivedAvailabilityStatus,
        bundle: bundleEntries,
        firstAvailableFutureDate: product?.firstAvailableFutureDate
          ? `- ${formatDate(String(product?.firstAvailableFutureDate), DateFormats.MEDIUM_DATE, locale)}`
          : '',
        isUnavailable: checkoutRestricted || cartPromoRestricted,
        unavailableText: checkoutRestricted ? unavailableText : notEligibleText,
        showError: !!checkoutRestricted,
        ...getVariantProps(entry, handleDropdown),
      });
    }
  });
  return { sopProducts, isOOS };
};

export const getIntialInstallmentDetails = (
  allInstallmentProducts: boolean,
  mainSubCart: SubCartDetail,
  installmentMasterConfigData: {
    installmentPlanIdFromConfig: string;
    installmentPlanTitleFromConfig: string;
  },
) => {
  const { installmentPlanIdFromConfig, installmentPlanTitleFromConfig } = installmentMasterConfigData;
  if (allInstallmentProducts && !mainSubCart.paymentDetails?.[0]?.paymentPlanId) {
    return {
      initialPlanId: installmentPlanIdFromConfig,
      initialPlanTitle: installmentPlanTitleFromConfig,
    };
  }
  return {
    initialPlanId: mainSubCart.paymentDetails?.[0]?.paymentPlanId as string,
    initialPlanTitle: mainSubCart.paymentDetails?.[0]?.paymentPlanTitle as string,
  };
};

export interface SelectedProducts {
  [prmId: string]: {
    [productId: string]: SelectedProduct;
  };
}
export interface SelectedProduct {
  /** @description Any id which will be required in payload creation. e.g. Cart entryId */
  entryId?: number;
  modified?: boolean;
  giftPromotion?: boolean;
  quantity: number;
}

export const getTransformedEntries = (payloadData: SelectedProducts) => {
  const entries: CartEntryDetails[] = [];
  Object.entries(payloadData).forEach(([prmId, productsObj]) => {
    Object.entries(productsObj).forEach(([productId, product]) => {
      if (!product?.entryId || product?.modified) {
        entries.push({
          ...(product?.entryId ? { entryId: product?.entryId } : {}),
          product: {
            id: productId,
          },
          quantity: product.quantity,
          isFreeItem: !!product?.giftPromotion,
          partnerPromotionId: prmId,
        });
      }
    });
  });
  return entries ?? [];
};

export const bottomMsgPresent = (promotionResults?: PromotionResults, productStockStatus?: string) => {
  const isBottomMsgPresent =
    (promotionResults?.potentialPromotions?.length ?? 0) > 0 ||
    (promotionResults?.firedPromotions?.length ?? 0) > 0 ||
    productStockStatus === StockStatusEnum.TNA ||
    productStockStatus === StockStatusEnum.NLA;

  return isBottomMsgPresent;
};

export const getUpdatedEntriesFromModificationArray = (modificationArray?: Modification[]) => {
  const entries: CartEntry[] = [];
  modificationArray?.map((modifiedArray: Modification) => {
    entries.push({
      entryId: modifiedArray?.entryId,
      product: {
        id: modifiedArray.product?.id || '',
      },
      quantity: modifiedArray?.quantity || 0,
      partnerPromotionId: modifiedArray?.partnerPromotionId || '',
      isOrphanedPromotionalEntry: Boolean(modifiedArray?.isOrphanedPromotionalEntry),
    });
  });

  return entries ?? [];
};

export const getPlanName = (planCode?: string, sopCMSData?: SubcriptionPlanCMSResponseType[]) => {
  const matchingPlan = sopCMSData?.find((data: SubcriptionPlanCMSResponseType) => data.plan_code === planCode);
  return matchingPlan?.plan_name;
};

export const isSellableCheck = (product?: Product) => {
  const value = product?.isSellable !== undefined && !product?.isSellable;
  return value || false;
};

export const hideVariantSelector = (
  data: CartResponse,
  entry: CartEntryDetails,
  shareCode: boolean,
  allEntries: CartEntryDetails[],
) => {
  const feeProduct = includesAny(entry, [AccountProductType.Fee]);
  // allEntries?.length > 1 when fee and kit product is merged
  // allEntries?.length > 2 when fee and kit product are two separate entries
  const sharebarFlow =
    shareCode &&
    data?.accountProcess?.type === AccountProcessType.Registration &&
    (feeProduct ? allEntries?.length > 1 : allEntries?.length > 2);

  const hideVariant =
    (sharebarFlow || data?.accountProcess?.type === AccountProcessType.Renewal) &&
    includesAny(entry, [AccountProductType.Ekit, AccountProductType.Pkit]);

  return hideVariant;
};

export const updateVariantGroupHandler = (variantGroup: VariantGroup, productOptions: SOPProduct[], price: number) => {
  const optionMapData = variantGroup?.optionMap?.filter(
    (variant: OptionMap) =>
      productOptions.some((option) =>
        option.isCategory ? variant?.product?.productId : option?.productId === variant?.product?.productId,
      ) && variant.product?.items?.[0]?.prices?.[0]?.priceIncludingTax === price,
  );

  const variantionOption = variantGroup?.variationOptions?.[0]?.options?.filter((optionData: Option) =>
    optionMapData?.some((option) => option?.optionChoice === optionData?.value),
  );

  const variantGroupData = {
    ...variantGroup,
    optionMap: optionMapData,
    variationOptions: [
      {
        type: variantGroup?.variationOptions?.[0].type,
        options: variantionOption,
      },
    ],
  };

  return variantGroupData;
};
